import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Grid, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon,
  FilterList as FilterIcon, FileDownload as ExportIcon, Add as AddIcon,
  CheckCircle as VerifiedIcon
} from "@mui/icons-material";
import { fetchData, deleteData, putData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import AddTeacherDialog from "./AddTeacherDialog";
import { exportToCSV } from "../../utils/tableUtils";

const GRADE_OPTIONS = [
  { value: "", label: "All Grades" },
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const AdminMaster = () => {
  const [tabValue, setTabValue] = useState(0);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [paraProfessionals, setParaProfessionals] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", grade: "", status: "" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [confirmClose, setConfirmClose] = useState({ open: false, student: null });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadStudents = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.grade) params.append("grade", filters.grade);
      if (filters.status) params.append("status", filters.status);

      const result = await fetchData(`admin/master/students?${params.toString()}`);
      if (result?.data) {
        setStudents(result.data.students || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load students" });
    }
  }, [filters]);

  const loadTeachers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.grade) params.append("grade", filters.grade);

      const result = await fetchData(`admin/master/teachers?${params.toString()}`);
      if (result?.data) {
        setTeachers(result.data.teachers || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load teachers" });
    }
  }, [filters]);

  const loadParaProfessionals = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10, role: "para-professional" });
      if (filters.search) params.append("search", filters.search);
      const result = await fetchData(`admin/master/teachers?${params.toString()}`);
      if (result?.data) {
        setParaProfessionals(result.data.teachers || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load para-professionals" });
    }
  }, [filters]);

  useEffect(() => {
    if (tabValue === 0) loadStudents();
    else if (tabValue === 1) loadTeachers();
    else if (tabValue === 2) loadParaProfessionals();
  }, [tabValue, loadStudents, loadTeachers, loadParaProfessionals]);

  const handleDelete = async () => {
    if (selectedItems.length === 0) return;
    try {
      for (const id of selectedItems) {
        const endpoint = tabValue === 0 ? `admin/master/students/${id}` : `admin/master/teachers/${id}`;
        await deleteData(endpoint);
      }
      setToast({ open: true, severity: "success", message: `${selectedItems.length} item(s) deleted` });
      setSelectedItems([]);
      if (tabValue === 0) loadStudents();
      else if (tabValue === 1) loadTeachers();
      else loadParaProfessionals();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const handleCloseRecord = async (student) => {
    try {
      await putData(`admin/master/close/${student._id || student.id}`, {});
      setToast({ open: true, severity: "success", message: "Record closed successfully" });
      setConfirmClose({ open: false, student: null });
      loadStudents();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to close record" });
      setConfirmClose({ open: false, student: null });
    }
  };

  const handleTeacherSuccess = (message) => {
    setToast({ open: true, severity: "success", message });
    setTeacherDialogOpen(false);
    setSelectedTeacher(null);
    loadTeachers();
  };

  const studentColumns = [
    { label: "Reg No", field: "regNo" },
    { label: "Student Name", field: "studentName" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Academic Year", field: "academicYear" },
    { label: "Parent Name", field: "parentName" },
    { label: "Payment", field: "paymentStatus" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const teacherColumns = [
    { label: "Teacher ID", field: "teacherId" },
    { label: "Teacher Name", field: "teacherName" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Designation", field: "designation" },
    { label: "Phone", field: "phone" },
    { label: "Action", field: "actions" }
  ];

  const studentRows = students.map((s) => ({
    ...s,
    regNo: s.registrationNumber || s.regNo || "-",
    studentName: s.studentName || `${s.firstName || ""} ${s.lastName || ""}`.trim() || "-",
    grade: s.grade || s.gradeApplyingFor || "-",
    section: s.section || "-",
    academicYear: s.academicYear || "2025-2026",
    parentName: s.parentName || "-",
    paymentStatus: s.paymentVerified ? (
      <Chip icon={<VerifiedIcon />} label="Verified" size="small"
        sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 600 }} />
    ) : (
      <Chip label="Pending" size="small"
        sx={{ backgroundColor: "#fff3e0", color: "#ff9800", fontWeight: 600 }} />
    ),
    statusChip: (
      <Chip label={s.status === "approved" ? "Approved" : s.status === "rejected" ? "Rejected" : s.status || "Active"}
        size="small" sx={{
          backgroundColor: s.status === "approved" ? "#e8f5e9" : s.status === "rejected" ? "#fce4ec" : "#e8f5e9",
          color: s.status === "approved" ? "#4caf50" : s.status === "rejected" ? "#f44336" : "#4caf50",
          fontWeight: 600
        }} />
    ),
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="View">
          <IconButton size="small" onClick={() => {}}>
            <ViewIcon fontSize="small" sx={{ color: "#244C77" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close Record">
          <Button size="small" variant="contained"
            onClick={() => setConfirmClose({ open: true, student: s })}
            sx={{
              minWidth: 58, px: 1, py: 0.25, fontSize: 12, fontWeight: 700,
              backgroundColor: "#f44336", textTransform: "none",
              "&:hover": { backgroundColor: "#d32f2f" }
            }}>
            Close
          </Button>
        </Tooltip>
      </Box>
    )
  }));

  const teacherRows = teachers.map((t) => ({
    ...t,
    teacherId: t.teacherId || t.id || "-",
    teacherName: t.teacherName || t.fullName || t.name || "-",
    grade: t.grade || "-",
    section: t.section || "-",
    designation: t.designation || "-",
    phone: t.phone || "-",
    actions: (
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => { setSelectedTeacher(t); setTeacherDialogOpen(true); }}>
          <EditIcon fontSize="small" sx={{ color: "#244C77" }} />
        </IconButton>
      </Tooltip>
    )
  }));

  const paraProfessionalColumns = [
    { label: "Teacher ID", field: "teacherId" },
    { label: "Name", field: "teacherName" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Designation", field: "designation" },
    { label: "Phone", field: "phone" },
    { label: "Action", field: "actions" }
  ];

  const paraProfessionalRows = paraProfessionals.map((t) => ({
    ...t,
    teacherId: t.teacherId || t.id || "-",
    teacherName: t.teacherName || t.fullName || t.name || "-",
    grade: t.grade || "-",
    section: t.section || "-",
    designation: t.designation || "-",
    phone: t.phone || "-",
    actions: (
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => { setSelectedTeacher(t); setTeacherDialogOpen(true); }}>
          <EditIcon fontSize="small" sx={{ color: "#244C77" }} />
        </IconButton>
      </Tooltip>
    )
  }));

  const handleExport = () => {
    if (tabValue === 0) {
      exportToCSV(studentColumns, studentRows, "students_master.csv");
    } else if (tabValue === 1) {
      exportToCSV(teacherColumns, teacherRows, "teachers_master.csv");
    } else {
      exportToCSV(paraProfessionalColumns, paraProfessionalRows, "para_professionals_master.csv");
    }
    setToast({ open: true, severity: "success", message: "Export started..." });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Master Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={0.5}>Master Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>List of Students Enrolled</Typography>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setFilters({ search: "", grade: "", status: "" }); }}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Student" />
        <Tab label="Teacher" />
        <Tab label="Para-Professional" />
      </Tabs>

      {/* Top Actions */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { if (tabValue === 0) loadStudents(); else if (tabValue === 1) loadTeachers(); else loadParaProfessionals(); } }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Grade</InputLabel>
            <Select value={filters.grade} label="Grade"
              onChange={(e) => setFilters(p => ({ ...p, grade: e.target.value }))}>
              {GRADE_OPTIONS.map(opt => (
                <MenuItem key={opt.value !== undefined ? opt.value : opt} value={opt.value !== undefined ? opt.value : opt}>
                  {opt.label || opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />}
            onClick={() => { if (tabValue === 0) loadStudents(); else if (tabValue === 1) loadTeachers(); else loadParaProfessionals(); }}
            sx={{ height: 40 }}>
            Apply
          </Button>
        </Grid>
        <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {selectedItems.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}
              sx={{ textTransform: "none" }}>
              Delete ({selectedItems.length})
            </Button>
          )}
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none" }}>
            Export
          </Button>
          {(tabValue === 1 || tabValue === 2) && (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setSelectedTeacher(null); setTeacherDialogOpen(true); }}
              sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
              {tabValue === 2 ? "Add Para-Professional" : "Add Teacher"}
            </Button>
          )}
        </Grid>
      </Grid>

      {/* Table */}
      {tabValue === 0 ? (
        <Dynamictablepagination columns={studentColumns} rows={studentRows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadStudents(newPage)} />
      ) : tabValue === 1 ? (
        <Dynamictablepagination columns={teacherColumns} rows={teacherRows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadTeachers(newPage)} />
      ) : (
        <Dynamictablepagination columns={paraProfessionalColumns} rows={paraProfessionalRows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadParaProfessionals(newPage)} />
      )}

      {/* Confirm Close Dialog */}
      <Dialog open={confirmClose.open}
        onClose={() => setConfirmClose({ open: false, student: null })}
        maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Close Record</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to close this record
            {confirmClose.student
              ? ` for ${confirmClose.student.studentName ||
                  `${confirmClose.student.firstName || ""} ${confirmClose.student.lastName || ""}`.trim() ||
                  "this student"}`
              : ""}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose({ open: false, student: null })}
            variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleCloseRecord(confirmClose.student)}
            variant="contained"
            sx={{ backgroundColor: "#f44336", textTransform: "none",
              "&:hover": { backgroundColor: "#d32f2f" } }}>
            Close Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Teacher Dialog */}
      <AddTeacherDialog
        open={teacherDialogOpen}
        onClose={() => { setTeacherDialogOpen(false); setSelectedTeacher(null); }}
        teacher={selectedTeacher}
        onSuccess={handleTeacherSuccess}
      />

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminMaster;
